import { useTranslation } from "react-i18next"
import { captureException } from "../services/exception-logger"

// All placeholders will be %s
const placeholder = "%s"
const isDevelopment = process.env.GATSBY_NODE_ENV === "development"

const handleError = (message: string) => {
  if (isDevelopment) {
    throw new Error(message)
  } else {
    captureException(new Error(message))
  }
}

export type DynamicTranslationFunction = (
  key: string,
  replacements: (string | number | boolean)[]
) => string

// Replacements has to be in correct order. E.g. if translation is "I am a human %s from %s", then replacements has to be ["being", "Denmark"] for it to place the replacements correctly.
export const useDynamicTranslation = (): DynamicTranslationFunction => {
  const {
    t,
    i18n: { exists },
  } = useTranslation()
  const tDynamic = (
    key: string,
    replacements: (string | number | boolean)[]
  ) => {
    const keyExists = exists(key)
    if (!keyExists) {
      handleError("Translation key does not exist.")
    }
    const rawTranslation = t(key)
    const regex = new RegExp(placeholder, "g")
    const numberOfPlaceholders = rawTranslation.match(regex)?.length
    const inaccurateReplacements = numberOfPlaceholders !== replacements.length
    if (inaccurateReplacements) {
      handleError(
        `You provided ${replacements.length} replacements, but need to provide ${numberOfPlaceholders} for ${rawTranslation}.`
      )
    }
    let translation = rawTranslation
    replacements.forEach((replacement) => {
      translation = translation.replace(
        placeholder,
        replacement ? replacement.toString() : ""
      )
    })
    return translation
  }
  return tDynamic
}
